import * as React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectors as authSelectors } from 'services/auth';
import { actions as kioskActions, selectors as kioskSelectors, Kiosk } from 'services/kiosk';
import { selectors as hardwareSelectors } from 'services/hardware';
import { routes } from '../routesArray';
import TicketingRoutes from './TicketingRoutes';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { selectors as configSelectors, actions as configActions } from 'services/configuration';
import { actions as salesErrorActions } from 'services/salesErrors';
import { selectors as cartSelectors, actions as cartActions } from 'services/cart';
import { useActions } from 'hooks/useActions';
import get from 'lodash.get';

const ProtectedRoutes = () => {
  // initial Load checks if this is the first time this is rendered
  const isInitialLoad = React.useRef(true);
  const initial = isInitialLoad && isInitialLoad.current;

  // Action Creators and Selectors
  const { getKiosk, getConfig, updateKiosk, resetSalesError, resetCart, releaseCart } = useActions({
    getKiosk: kioskActions.getKiosk,
    getConfig: configActions.getConfig,
    updateKiosk: kioskActions.updateKiosk,
    resetSalesError: salesErrorActions.resetError,
    resetCart: cartActions.resetCart,
    releaseCart: cartActions.releaseCart
  });
  const location = useLocation();
  const config = useSelector(configSelectors.selectConfiguration);
  const loading = useSelector(configSelectors.selectLoading);
  const kioskLoading = useSelector(kioskSelectors.selectLoading);
  const kiosk = useSelector(kioskSelectors.selectKiosk) as Kiosk;
  const inactiveKioskHardwareId = useSelector(kioskSelectors.selectInactiveKioskHardwareId);
  const admin = useSelector(authSelectors.selectAdminId);
  const anyHardwareErrors = useSelector(hardwareSelectors.selectAnyHardwareErrors);
  const cart = useSelector(cartSelectors.selectCart);
  const previewEnabled = get(kiosk, 'configuration.kioskPreview.enabled');
  const isPreviewMode = sessionStorage.getItem('mode') === 'preview';
  const token = sessionStorage.getItem('token');

  // passing palette, fonts to Mui Theme

  // on mount gets the configuration object for the kiosk and sets initial load to false
  React.useEffect(() => {
    if (!(isPreviewMode && token) && kiosk) {
      getKiosk({ kioskId: kiosk.id });
      getConfig({ configurationId: kiosk.configurationId });
    }
    isInitialLoad.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    // Remove preview mode flag when kioskPreview is disabled in kiosk config
    if (kiosk && isPreviewMode && !previewEnabled) {
      sessionStorage.removeItem('mode');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewEnabled]);

  React.useEffect(() => {
    if (kiosk && kiosk.status !== 'Inactive' && anyHardwareErrors && !kioskLoading) {
      updateKiosk({
        status: 'Inactive',
        inactiveKioskHardwareId
      });
      resetSalesError();
      if (cart) {
        releaseCart(cart.releaseParams);
        resetCart();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // if the token is expired, redirect to home
  if (!admin) return <Navigate to="/" />;

  // if this is the initial load or loading the configurations, show loading overlay
  if (initial || loading) return <LoadingOverlay />;

  // if there is no configuration, redirect to setup page (SetUpView)
  if (!config) return <Navigate to="/" />;

  return (
    <Routes>
      {/* Routes that are protected but not ticketing */}
      {routes
        .filter((route) => !route.ticketing && route.protected)
        .map((route) => (
          <Route key={route.path} path={route.path} element={<route.component />} />
        ))}
      {/* TicketingRoutes Component */}
      <Route path="*" element={<TicketingRoutes />} />
    </Routes>
  );
};

export default ProtectedRoutes;
